import {
  BATTERY_GROSS_USD,
  BATTERY_ITC_RATE,
  BATTERY_ROUND_TRIP_EFFICIENCY,
  BATTERY_USABLE_KWH,
  DAYS_IN_MONTH,
  SOLAR_EXPORT_RATE_FACTOR,
  TOU_SPREAD_FACTOR,
} from './assumptions';
import { consumptionKwhForMonth, splitSolarMonth } from './solarSplit';
import { EnergyLoads, MeasureApplication, SimulationContext } from './types';

// Monthly-resolution approximations of daily battery dispatch — no hourly sim.
const monthlySavingsUsd = (loads: EnergyLoads, ctx: SimulationContext, month: number): number => {
  const days = DAYS_IN_MONTH[month];
  const monthlyCapacityKwh = BATTERY_USABLE_KWH * days;

  if (ctx.toggles.batteryMode === 'ARBITRAGE') {
    // One full cycle per day, buying off-peak and offsetting peak usage.
    return BATTERY_USABLE_KWH * BATTERY_ROUND_TRIP_EFFICIENCY * days * ctx.ratePerKwhUsd * TOU_SPREAD_FACTOR;
  }

  if (ctx.toggles.batteryMode === 'SELF_USE') {
    // Shift solar that would otherwise export back into evening consumption,
    // upgrading its value from the export rate to the retail rate.
    const { exportKwh, importKwh } = splitSolarMonth(consumptionKwhForMonth(loads, month), loads.solarKwh[month]);
    const shiftedKwh = Math.min(exportKwh, importKwh, monthlyCapacityKwh) * BATTERY_ROUND_TRIP_EFFICIENCY;
    return shiftedKwh * ctx.ratePerKwhUsd * (1 - SOLAR_EXPORT_RATE_FACTOR);
  }

  // BACKUP holds its charge in reserve; resilience value, no bill savings.
  return 0;
};

export const applyBattery = (loads: EnergyLoads, ctx: SimulationContext): MeasureApplication => {
  const incentiveUsd = BATTERY_GROSS_USD * BATTERY_ITC_RATE;

  return {
    loads: {
      ...loads,
      batterySavingsUsd: loads.batterySavingsUsd.map((usd, month) => usd + monthlySavingsUsd(loads, ctx, month)),
    },
    measureCost: {
      grossUsd: BATTERY_GROSS_USD,
      incentiveUsd,
      measure: 'BATTERY',
      netUsd: BATTERY_GROSS_USD - incentiveUsd,
    },
  };
};
